import styled from '@mui/material/styles/styled';
import { colors } from '@sweep-io/sweep-design';
import { useRef, useEffect, useCallback, memo, useState } from 'react';
import { Stack, Box } from '@mui/material';
import { MarkdownWrapper } from '../../MarkdownWrapper';
import { MarkdownWithReply } from '../../MarkdownWithReply';
import { useRunOnce } from '../../useRunOnce';
import { PdfExportButton } from './PdfExportButton';
import { RichTextCopyButton } from './RichTextCopyButton';
import { DataTableVariant } from '../../table/TableTypes';
import { ReasoningSection } from './ReasoningSection';
import { useTypewriter } from '../../../../hooks/useTypewriter';
import { AiChatMessage } from '../aiChatTypes';
import { getTextContent, getArtifactParts } from '../aiChatHelpers';
import { ArtifactPreview } from '../../artifacts/ArtifactPreview';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { highlightMatchInMarkdown } from '../../../../lib/highlightMatchInMarkdown';
import { useArtifactsOptional } from '../../artifacts/useArtifacts';

const MESSAGE_STYLE = {
  pr: 1.5,
  pl: 1.5,
};

const AiChatAssistantMessageBoxComponent = ({
  message,
  onMount,
  onTextSelection,
  isStreamingResponse = false,
  chatName,
  dataTableVariant = DataTableVariant.small,
  onAnimationProgressPersistence,
  searchTermToHighlight,
  artifactVersionMap,
}: {
  message: AiChatMessage;
  onMount?: () => void;
  onTextSelection?: (text: string | null) => void;
  isStreamingResponse?: boolean;
  chatName?: string;
  dataTableVariant?: DataTableVariant;
  onAnimationProgressPersistence?: (progress: {
    messageCharIndex: number;
    reasoningChunkIndex: number;
  }) => void;
  searchTermToHighlight?: string;
  artifactVersionMap?: Map<string, number>;
}) => {
  const content = message.content ?? '';
  const reasoningChunks = message.reasoningChunks;
  const errorState = message.isError ?? false;
  const { artifacts: artifactsFeatureFlag } = useFeatureToggle();
  const artifactContext = useArtifactsOptional();
  const artifacts = artifactContext?.artifacts ?? {};

  // Check for artifacts in two ways:
  // 1. Real-time streaming: contentType === 'artifact' with artifactData
  // 2. Historical from DB: structured content with artifact parts
  const isStreamingArtifact =
    artifactsFeatureFlag && message.contentType === 'artifact' && message.artifactData;
  const historicalArtifacts = artifactsFeatureFlag ? getArtifactParts(content) : [];
  const hasArtifacts = isStreamingArtifact || historicalArtifacts.length > 0;

  const messageRef = useRef<HTMLDivElement>(null);
  const resumeMessageAnimationFromPosition = message?.animationProgress?.messageCharIndex || 0;
  const resumeReasoningChunkFromIndex = message?.animationProgress?.reasoningChunkIndex || 0;
  const [isOrWasStreaming] = useState(isStreamingResponse);

  // Track current progress for unmount persistence
  const currentMessageCharRef = useRef(resumeMessageAnimationFromPosition);
  const currentReasoningChunkRef = useRef(resumeReasoningChunkFromIndex);

  // Persist animation progress when component unmounts
  useEffect(() => {
    return () => {
      // Only persist if we were actually animating and callback is provided
      if ((isStreamingResponse || reasoningChunks) && onAnimationProgressPersistence) {
        onAnimationProgressPersistence({
          messageCharIndex: currentMessageCharRef.current,
          reasoningChunkIndex: currentReasoningChunkRef.current,
        });
      }
    };
  }, [isStreamingResponse, reasoningChunks, onAnimationProgressPersistence]);

  const handleProgressUpdate = useCallback((characterIndex: number) => {
    currentMessageCharRef.current = characterIndex;
  }, []);

  const handleReasoningChunkProgress = useCallback((chunkIndex: number) => {
    currentReasoningChunkRef.current = chunkIndex;
  }, []);

  // Extract text content (handles both string and structured content)
  const textContent = getTextContent(content);

  // Apply search highlighting if needed
  const highlightedTextContent = searchTermToHighlight
    ? highlightMatchInMarkdown(textContent, searchTermToHighlight)
    : textContent;

  const { displayedText, isComplete: isTypewriterComplete } = useTypewriter(
    isStreamingResponse ? highlightedTextContent : '',
    {
      autoStart: isStreamingResponse,
      supportStreaming: true,
      resumeFromPosition: resumeMessageAnimationFromPosition,
      onProgressUpdate: handleProgressUpdate,
    },
  );

  // Determine the actual content to display
  const displayContent = isStreamingResponse ? displayedText : highlightedTextContent;

  // Don't show buttons bar for messages with artifacts
  const shouldShowButtonsBar = hasArtifacts
    ? false
    : isOrWasStreaming
      ? !isStreamingResponse && isTypewriterComplete
      : true;

  useRunOnce(() => {
    onMount?.();
  });

  const markdownWrapperProps = {
    content: displayContent,
    color: errorState ? colors.blush[600] : undefined,
    removeMargins: true,
    dataTableVariant,
    shouldShowTableCopyButtons: shouldShowButtonsBar,
  };

  const renderContent = () => {
    // Real-time streaming artifact (shows text + artifact box)
    if (isStreamingArtifact) {
      return (
        <Stack gap={1}>
          {displayContent && (
            <MarkdownWrapper {...markdownWrapperProps} shouldShowTableCopyButtons={false} />
          )}
          <ArtifactPreview artifactData={message.artifactData!} />
        </Stack>
      );
    }

    // Historical artifacts with structured content (render in order)
    if (historicalArtifacts.length > 0 && Array.isArray(content)) {
      return (
        <Stack gap={1}>
          {content.map((part, idx) => {
            if (part.type === 'text') {
              // Apply highlighting to text parts
              const textContent = searchTermToHighlight
                ? highlightMatchInMarkdown(part.text, searchTermToHighlight)
                : part.text;

              return (
                <MarkdownWrapper
                  key={`text-${idx}`}
                  content={textContent}
                  color={errorState ? colors.blush[600] : undefined}
                  removeMargins={true}
                  dataTableVariant={dataTableVariant}
                  shouldShowTableCopyButtons={false}
                />
              );
            } else if (part.type === 'artifact') {
              // Get version number from version map (based on order in chat)
              // or from stored artifact if it was already fetched
              const existingArtifact = artifacts?.[part.artifactId];
              const versionFromStore = existingArtifact?.versions.find(
                (v) => v.versionId === part.versionId,
              )?.version;
              const versionFromMap = artifactVersionMap?.get(part.versionId);
              const version = versionFromStore ?? versionFromMap;

              return (
                <ArtifactPreview
                  key={`artifact-${idx}`}
                  artifactData={{
                    artifactId: part.artifactId,
                    title: part.title.replace(/^"|"$/g, ''), // Remove surrounding quotes
                    versionId: part.versionId,
                    version: version,
                  }}
                />
              );
            }
            return null;
          })}
        </Stack>
      );
    }

    // No artifacts - render normally
    if (onTextSelection) {
      return (
        <MarkdownWithReply
          markdownWrapperProps={markdownWrapperProps}
          onTextSelection={onTextSelection}
        />
      );
    }

    return <MarkdownWrapper {...markdownWrapperProps} />;
  };

  return (
    <Stack gap={1} data-message-type="assistant" data-testid="ai-chat-assistant-message-box">
      <Box sx={MESSAGE_STYLE}>
        {reasoningChunks && (
          <ReasoningSection
            reasoningChunks={reasoningChunks}
            isStreamingReasoning={isStreamingResponse && !content}
            resumeFromChunkIndex={resumeReasoningChunkFromIndex}
            onChunkProgressUpdate={handleReasoningChunkProgress}
          />
        )}
      </Box>

      <Box
        display="flex"
        gap={0.5}
        alignItems="flex-start"
        width={hasArtifacts ? '100%' : undefined}
      >
        <AssistantMessageBox
          className="message-content-box"
          ref={messageRef}
          sx={{
            ...(hasArtifacts ? {} : MESSAGE_STYLE),
            ...(hasArtifacts && {
              flex: 1,
              width: '100%',
            }),
          }}
        >
          {renderContent()}
        </AssistantMessageBox>
      </Box>
      {shouldShowButtonsBar && (
        <Box
          className="buttons-bar"
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            ml: '6px', //the button's icon needs to be aligned to the message's text
          }}
        >
          <RichTextCopyButton messageRef={messageRef} />
          <PdfExportButton messageRef={messageRef} chatName={chatName} />
        </Box>
      )}
    </Stack>
  );
};

const AssistantMessageBox = styled(Box)({
  maxWidth: '100%',
});

export const AiChatAssistantMessageBox = memo(AiChatAssistantMessageBoxComponent);
